import clsx from 'clsx';
import { useEffect, useState, type CSSProperties } from 'react';
import { PageTitle, useLayout } from '../../../_metronic/layout/core';
import { DefaultTitle } from '../../../_metronic/layout/components/header/page-title/DefaultTitle';

/* Tradução */
import './../../../i18n/config';
import { useTranslation } from 'react-i18next';

import DashboardConciliacao from './DashboardConciliacao';
import DashboardTempoUso from './DashboardTempoUso';
import DashboardContratosLancamentos from './DashboardContratosLancamentos';
import DashboardConciliacaoContabilFinanceira from './DashboardConciliacaoContabilFinanceira';
import type { DashboardFiltros, TipoPeriodoFiltro } from './types';
import type { IEmpresas } from '../../interfaces/IEmpresas';
import EmpresasService from '../../services/EmpresasService';
import DashboardService from '../../services/DashboardService';
import StorageUtils from '../../utils/StorageUtils';

type TipoDashboard =
    | 'conciliacao'
    | 'tempoUso'
    | 'financeiro'
    | 'conciliacaoFinanceira';

const Dashboard = () => {
    const { classes } = useLayout();
    const { t } = useTranslation();

    const [empresaSelecionada, setEmpresaSelecionada] = useState<number>(0);
    const [dashboardAtivo, setDashboardAtivo] = useState<TipoDashboard>('conciliacao');

    const [tipoPeriodo, setTipoPeriodo] = useState<TipoPeriodoFiltro>('ano');
    const [anoSelecionado, setAnoSelecionado] = useState<number>(0);
    const [mesSelecionado, setMesSelecionado] = useState<string>('');
    const [diaSelecionado, setDiaSelecionado] = useState<string>('');
    const [dataInicial, setDataInicial] = useState<string>('');
    const [dataFinal, setDataFinal] = useState<string>('');

    const [empresas, setEmpresas] = useState<IEmpresas[]>([]);
    const [carregandoEmpresas, setCarregandoEmpresas] = useState<boolean>(true);

    useEffect(() => {
        async function carregarEmpresas() {
            setCarregandoEmpresas(true);
            try {
                const usuario = await StorageUtils.getUsuario();

                if (usuario?.perfil === 'Analista') {
                    const resultado = await EmpresasService.obterTodosPorUsuarioId(usuario.id);
                    setEmpresas(resultado);
                } else if (usuario?.clienteID) {
                    const resultado = await EmpresasService.obterTodosPorClienteID(usuario.clienteID);
                    setEmpresas(resultado);
                }
            } finally {
                setCarregandoEmpresas(false);
            }
        }

        carregarEmpresas();
    }, []);

    const [anos, setAnos] = useState<number[]>([]);
    const [carregandoAnos, setCarregandoAnos] = useState<boolean>(false);

    useEffect(() => {
        setAnoSelecionado(0);

        if (!empresaSelecionada) {
            setAnos([]);
            return;
        }

        let ativo = true;

        async function carregarAnos() {
            setCarregandoAnos(true);
            try {
                const anosDisponiveis = await DashboardService.obterAnosDisponiveis(empresaSelecionada);
                if (ativo) setAnos(anosDisponiveis);
            } finally {
                if (ativo) setCarregandoAnos(false);
            }
        }

        carregarAnos();

        return () => {
            ativo = false;
        };
    }, [empresaSelecionada]);

    const tiposPeriodo: { value: TipoPeriodoFiltro; label: string }[] = [
        { value: 'ano', label: 'Ano' },
        { value: 'mes', label: 'Mês' },
        { value: 'dia', label: 'Dia' },
        { value: 'personalizado', label: 'Personalizado' },
    ];

    const cores = {
        azulPrincipal: '#1f5eff',
        azulHover: '#174bcc',
        cinzaBorda: '#e9ecef',
        fundoCard: '#ffffff',
        cinzaTexto: '#5f6368',
        titulo: '#3c4043',
        fundoAbaInativa: '#f5f6fa',
    };

    useEffect(() => {
        setAnoSelecionado(0);
        setMesSelecionado('');
        setDiaSelecionado('');
        setDataInicial('');
        setDataFinal('');
    }, [tipoPeriodo]);

    const filtrosPreenchidos = empresaSelecionada > 0;

    const filtros: DashboardFiltros = {
        empresaSelecionada,
        tipoPeriodo,
        anoSelecionado,
        mesSelecionado,
        diaSelecionado,
        dataInicial,
        dataFinal,
    };

    const cardStyle: CSSProperties = {
        background: cores.fundoCard,
        border: `1px solid ${cores.cinzaBorda}`,
        borderRadius: '14px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        padding: '14px 16px',
    };

    const botaoAba = (ativo: boolean): CSSProperties => ({
        border: `1px solid ${ativo ? cores.azulPrincipal : cores.cinzaBorda}`,
        background: ativo ? cores.azulPrincipal : cores.fundoAbaInativa,
        color: ativo ? '#ffffff' : '#4b4f52',
        borderRadius: '10px',
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    });

    const renderFiltroPeriodo = () => {
        if (tipoPeriodo === 'ano') {
            return (
                <div className='col-sm-12 col-md-3 col-lg-2'>
                    <div className='form-group'>
                        <label>{t('Filtro por Ano')}</label>
                        <select
                            className='form-control'
                            value={anoSelecionado}
                            onChange={(e) => setAnoSelecionado(parseInt(e.target.value))}
                            disabled={!empresaSelecionada || carregandoAnos}
                        >
                            <option value={0}>{t('Todos')}</option>
                            {anos.map((ano) => (
                                <option key={ano} value={ano}>
                                    {ano}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            );
        }

        if (tipoPeriodo === 'mes') {
            return (
                <div className='col-sm-12 col-md-3 col-lg-2'>
                    <div className='form-group'>
                        <label>{t('Filtro por Mês')}</label>
                        <input
                            type='month'
                            className='form-control'
                            value={mesSelecionado}
                            onChange={(e) => setMesSelecionado(e.target.value)}
                            disabled={!empresaSelecionada}
                        />
                    </div>
                </div>
            );
        }

        if (tipoPeriodo === 'dia') {
            return (
                <div className='col-sm-12 col-md-3 col-lg-2'>
                    <div className='form-group'>
                        <label>{t('Filtro por Dia')}</label>
                        <input
                            type='date'
                            className='form-control'
                            value={diaSelecionado}
                            onChange={(e) => setDiaSelecionado(e.target.value)}
                            disabled={!empresaSelecionada}
                        />
                    </div>
                </div>
            );
        }

        if (tipoPeriodo === 'personalizado') {
            return (
                <>
                    <div className='col-sm-12 col-md-3 col-lg-2'>
                        <div className='form-group'>
                            <label>{t('Data Inicial')}</label>
                            <input
                                type='date'
                                className='form-control'
                                value={dataInicial}
                                onChange={(e) => setDataInicial(e.target.value)}
                                disabled={!empresaSelecionada}
                            />
                        </div>
                    </div>

                    <div className='col-sm-12 col-md-3 col-lg-2'>
                        <div className='form-group'>
                            <label>{t('Data Final')}</label>
                            <input
                                type='date'
                                className='form-control'
                                value={dataFinal}
                                onChange={(e) => setDataFinal(e.target.value)}
                                disabled={!empresaSelecionada}
                            />
                        </div>
                    </div>
                </>
            );
        }

        return null;
    };

    return (
        <>
            <PageTitle breadcrumbs={[]}>{t('Dashboard')}</PageTitle>

            <div className='toolbar' id='kt_toolbar'>
                <div
                    id='kt_toolbar_container'
                    className={clsx(classes.toolbarContainer.join(' '), 'd-flex flex-stack')}
                >
                    <DefaultTitle />
                </div>
            </div>

            <div className='container-fluid'>
                <div className='row mb-5'>
                    <div className='col-12'>
                        <div style={cardStyle}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '12px',
                                }}
                            >
                                <button
                                    type='button'
                                    style={botaoAba(dashboardAtivo === 'conciliacao')}
                                    onClick={() => setDashboardAtivo('conciliacao')}
                                >
                                    Dashboard - Conciliação
                                </button>

                                <button
                                    type='button'
                                    style={botaoAba(dashboardAtivo === 'conciliacaoFinanceira')}
                                    onClick={() => setDashboardAtivo('conciliacaoFinanceira')}
                                >
                                    Dashboard - Conciliação Contábil e Financeira
                                </button>

                                <button
                                    type='button'
                                    style={botaoAba(dashboardAtivo === 'financeiro')}
                                    onClick={() => setDashboardAtivo('financeiro')}
                                >
                                    Dashboard - Contratos de Lançamentos
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='row mb-6'>
                    <div className='col-sm-12 col-md-4 col-lg-3'>
                        <div className='form-group'>
                            <label>{t('Empresa')}</label>
                            <select
                                className='form-control'
                                value={empresaSelecionada}
                                onChange={(e) => setEmpresaSelecionada(parseInt(e.target.value))}
                                disabled={carregandoEmpresas}
                            >
                                <option value={0}>
                                    {carregandoEmpresas ? t('Carregando...') : t('Selecione uma Empresa')}
                                </option>
                                {empresas.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.razaoSocial}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className='col-sm-12 col-md-3 col-lg-2'>
                        <div className='form-group'>
                            <label>{t('Tipo de Filtro')}</label>
                            <select
                                className='form-control'
                                value={tipoPeriodo}
                                onChange={(e) => setTipoPeriodo(e.target.value as TipoPeriodoFiltro)}
                                disabled={!empresaSelecionada}
                            >
                                {tiposPeriodo.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {renderFiltroPeriodo()}
                </div>

                {!filtrosPreenchidos && (
                    <div className='row'>
                        <div className='col-12'>
                            <div style={cardStyle}>
                                <span style={{ color: cores.cinzaTexto, fontSize: '14px' }}>
                                    Selecione a <strong>empresa</strong> para visualizar o dashboard.
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {filtrosPreenchidos && (
                    <>
                        {dashboardAtivo === 'conciliacao' && <DashboardConciliacao filtros={filtros} />}

                        {dashboardAtivo === 'tempoUso' && <DashboardTempoUso filtros={filtros} />}

                        {dashboardAtivo === 'financeiro' && <DashboardContratosLancamentos filtros={filtros} />}

                        {dashboardAtivo === 'conciliacaoFinanceira' && (
                            <DashboardConciliacaoContabilFinanceira filtros={filtros} />
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default Dashboard;
